// Выбор цели подготовки: какой экзамен и когда. Решение владельца от 30 октября
// 2026 (docs/02-decisions.md): цель — переменная, лицей БГУ в июне — один её
// вариант. Список целей и что они значат для карты — convex/lib/targets.ts.
//
// Один экран на два места: первым шагом до диагностики и «Сменить цель» в
// «Личных данных». Во втором месте он говорит цену: дата экзамена — то, от
// чего считаются темп плана и сроки повторений.

import { useState } from "react";
import { api, useMutation } from "../api";
import { reasonOf } from "../lib/errors";
import { TARGET_IDS, TARGETS, TARGET_NAME_LIMIT, type TargetId } from "../../convex/lib/targets";

// Один источник целей с сервером: список, названия и пояснения — оттуда.
const OPTIONS = TARGET_IDS.map((id) => ({ id, ...TARGETS[id] }));

/** Ближайшее 15 июня, до которого больше месяца: экзамены 9 класса — в июне. */
function defaultDate(): string {
  const now = new Date();
  const year = now.getMonth() >= 4 ? now.getFullYear() + 1 : now.getFullYear();
  return `${year}-06-15`;
}

export function TargetPick({
  learnerKey,
  initial,
  change = false,
  onDone,
  onCancel,
}: {
  learnerKey: string;
  initial?: { target: TargetId | null; targetName: string | null; examDate: string } | null;
  /** Смена уже выбранной цели — экран называет цену. */
  change?: boolean;
  onDone: () => void;
  onCancel?: () => void;
}) {
  const setTarget = useMutation(api.goal.setTarget);
  const [target, setPicked] = useState<TargetId>(initial?.target ?? "entrance10");
  const [name, setName] = useState(initial?.targetName ?? "");
  const [date, setDate] = useState(initial?.examDate ?? defaultDate());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = () => {
    setBusy(true);
    setError(null);
    void setTarget({
      learnerKey,
      target,
      examDate: date,
      ...(target === "custom" ? { targetName: name } : {}),
    })
      .then(onDone)
      .catch((e: unknown) => setError(reasonOf(e)))
      .finally(() => setBusy(false));
  };

  return (
    <div className="target-pick">
      <div>
        {/* Первый экран нового ученика — знакомство с продуктом, поэтому здесь
            его слоган. При смене цели он уже знаком. */}
        <div className="eyebrow">{change ? "Цель подготовки" : "Знаешь? Докажи"}</div>
        <h1 className="h1">{change ? "Сменить цель" : "Что сдаёшь и когда"}</h1>
        <p className="lede">
          {change
            ? "От цели и даты считаются план по месяцам, темп и обратный отсчёт: после смены они пересчитаются. Пройденное не теряется."
            : "От этого зависят план по месяцам и то, какие темы система считает нужными. Поменять можно потом в «Личных данных»."}
        </p>
      </div>

      <div className="choices" role="radiogroup" aria-label="Цель подготовки">
        {OPTIONS.map((o) => (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={target === o.id}
            className={`choice${target === o.id ? " choice--on" : ""}`}
            onClick={() => setPicked(o.id)}
          >
            <span className="choice__mark" aria-hidden="true" />
            <span className="choice__text">
              <strong>{o.title}</strong>
              <span className="faint" style={{ display: "block", marginTop: 4, lineHeight: 1.45 }}>
                {o.hint}
              </span>
            </span>
          </button>
        ))}
      </div>

      <div className="card target-pick__fields">
        {target === "custom" ? (
          <label className="target-pick__field">
            <span className="card__label">Название цели</span>
            <input
              className="field field--text"
              value={name}
              maxLength={TARGET_NAME_LIMIT}
              placeholder="например, вступительный в гимназию № 1"
              onChange={(e) => setName(e.target.value)}
            />
          </label>
        ) : null}
        <label className="target-pick__field">
          <span className="card__label">Дата экзамена</span>
          <input className="field field--text" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <span className="faint">Точную дату не знаешь — поставь примерную, её можно поправить.</span>
        </label>
      </div>

      {error === null ? null : (
        <div className="notice" role="alert">
          {error}
        </div>
      )}

      <div className="row row--actions">
        <button
          type="button"
          className="btn btn--primary btn--big"
          disabled={busy || (target === "custom" && name.trim() === "")}
          onClick={save}
        >
          {change ? "Сохранить цель" : "Дальше"}
        </button>
        {onCancel ? (
          <button type="button" className="btn btn--quiet" onClick={onCancel}>
            Отмена
          </button>
        ) : null}
      </div>
    </div>
  );
}
